import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useChat } from "@/lib/chat";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { num } from "./shared";

const GROUPS = {
  "All activity": "",
  "Messages & threads": "message_sent,thread_reply_sent,message_edited,message_deleted,message_composed",
  "Typing": "keystroke,composer_typing,draft_snapshot,draft_abandoned,composer_paste,composer_started",
  "Reactions": "reaction_added,reaction_removed",
  "Clicks": "click,context_menu",
  "Reading": "conversation_viewed,messages_seen,conversation_dwell,thread_opened",
  "Presence": "heartbeat,session_start,session_end,window_focus,window_blur,visibility_change,login_success",
  "Huddles & files": "huddle_started,huddle_joined,huddle_left,huddle_ended,file_uploaded,file_attached,file_downloaded",
};

const summary = (e) => {
  const md = e.metadata || {};
  switch (e.event_type) {
    case "keystroke": return <><kbd className="tc-kbd">{md.key === " " ? "Space" : md.key}</kbd> <span className="muted">{md.class}{md.thread ? " · thread" : ""}</span></>;
    case "composer_typing": return `${num(md.keystrokes)} keys · +${num(md.chars_added)} / −${num(md.chars_deleted)} chars · ${md.backspaces} backspaces · ${Math.round((md.duration_ms || 0) / 1000)}s`;
    case "message_composed": return `${num(md.length)} chars in ${Math.round((md.duration_ms || 0) / 1000)}s · ${num(md.keystrokes)} keys${md.files ? ` · ${md.files} files` : ""}${md.efficiency != null ? ` · efficiency ${md.efficiency}` : ""}`;
    case "draft_snapshot":
    case "draft_abandoned": return <span className="tc-draft">“{md.text ? (md.text.length > 160 ? `${md.text.slice(0, 160)}…` : md.text) : `${md.length} chars`}”</span>;
    case "message_sent":
    case "thread_reply_sent": return `${num(md.length)} chars · ${md.words || 0} words${md.mentions ? ` · ${md.mentions} mentions` : ""}${md.files ? ` · ${md.files} files` : ""}`;
    case "reaction_added":
    case "reaction_removed": return `:${e.label}:`;
    case "click": return <>{e.label ? `“${e.label}”` : null} <span className="mono muted">{e.target}</span></>;
    case "messages_seen": return `${md.count} messages read`;
    case "conversation_dwell": return `${Math.round((md.duration_ms || 0) / 1000)}s visible · ${md.messages_seen} read · scrolled ${Math.round((md.max_scroll_up || 0) * 100)}% back`;
    case "heartbeat": return `active · ${md.interactions ?? 0} interactions`;
    case "huddle_ended": return `${Math.round((md.duration_sec || 0) / 60)} min`;
    default: return e.label || e.target || "";
  }
};

export const ActivityTimeline = ({ uid, params }) => {
  const { conversations } = useChat();
  const [group, setGroup] = useState("All activity");
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const convName = (cid) => { const c = conversations.find((x) => x.id === cid); return c ? (c.type === "channel" ? `#${c.name}` : c.display_name) : null; };
  const load = async (reset) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/performance/members/${uid}/activity`, { params: { ...params, limit: 50, event_type: GROUPS[group] || undefined, cursor: reset ? undefined : cursor || undefined } });
      setRows((r) => (reset ? data.events : [...r, ...data.events]));
      setCursor(data.next_cursor);
      setMore(data.has_more);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  useEffect(() => { load(true); }, [uid, params, group]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="tc-admin-card" data-testid="activity-timeline">
      <div className="tc-admin-card__head">Activity timeline
        <span className="right">
          <select className="tc-select sm" value={group} onChange={(e) => setGroup(e.target.value)} data-testid="timeline-filter">{Object.keys(GROUPS).map((g) => <option key={g}>{g}</option>)}</select>
        </span>
      </div>
      <table className="tc-table tc-timeline">
        <tbody>
          {rows.map((e) => (
            <tr key={e.id} data-testid={`timeline-row-${e.id}`}>
              <td className="muted mono" style={{ whiteSpace: "nowrap", width: 150 }}>{fmtDateTime(e.ts)}</td>
              <td style={{ width: 170 }}><span className={`tc-pill tc-pill--${e.category}`}>{e.event_type}</span></td>
              <td>{summary(e)}</td>
              <td className="muted" style={{ whiteSpace: "nowrap" }}>{e.conversation_id ? convName(e.conversation_id) || <span className="mono">{e.conversation_id.slice(-6)}</span> : ""}</td>
            </tr>
          ))}
          {!rows.length && !busy && <tr><td className="muted" style={{ textAlign: "center", padding: 24 }}>No activity in this period.</td></tr>}
        </tbody>
      </table>
      {more && <div style={{ padding: 12, textAlign: "center" }}><button className="tc-btn tc-btn--outline sm" disabled={busy} onClick={() => load(false)} data-testid="timeline-load-more">{busy ? "Loading…" : "Load older activity"}</button></div>}
    </div>
  );
};
